import * as React from "react";
import {
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import IconButton from "./IconButton";

export type Severity = "success" | "info" | "warning" | "error";

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  action?: React.ReactNode;
  closeText?: string;
  color?: Severity;
  severity?: Severity;
  icon?: React.ReactNode | false;
  iconMapping?: Partial<Record<Severity, React.ReactNode>>;
  onClose?: (event: React.SyntheticEvent) => void;
  variant?: "standard" | "filled" | "outlined";
}

const defaultIconMapping: Record<Severity, React.ReactNode> = {
  success: <CheckCircleIcon className="w-6 h-6" />,
  info: <InformationCircleIcon className="w-6 h-6" />,
  warning: <ExclamationTriangleIcon className="w-6 h-6" />,
  error: <XCircleIcon className="w-6 h-6" />,
};

const styles = {
  standard: {
    success: "bg-green-50 text-green-900",
    info: "bg-blue-50 text-blue-900",
    warning: "bg-amber-50 text-amber-900",
    error: "bg-red-50 text-red-900",
    iconColor: {
      success: "text-green-600",
      info: "text-blue-600",
      warning: "text-amber-600",
      error: "text-red-600",
    },
  },
  outlined: {
    success: "border border-green-500 text-green-900",
    info: "border border-blue-500 text-blue-900",
    warning: "border border-amber-500 text-amber-900",
    error: "border border-red-500 text-red-900",
    iconColor: {
      success: "text-green-600",
      info: "text-blue-600",
      warning: "text-amber-600",
      error: "text-red-600",
    },
  },
  filled: {
    success: "bg-green-600 text-white",
    info: "bg-blue-600 text-white",
    warning: "bg-amber-600 text-white",
    error: "bg-red-600 text-white",
    iconColor: {
      success: "text-white",
      info: "text-white",
      warning: "text-white",
      error: "text-white",
    },
  },
};

export default function Alert(props: AlertProps): JSX.Element {
  const {
    action,
    children,
    className = "",
    closeText = "Close",
    color,
    severity = "success",
    icon,
    iconMapping,
    onClose,
    role = "alert",
    variant = "standard",
    ...other
  } = props;

  const baseSeverity = color || severity;
  let resolvedIcon: React.ReactNode = null;

  if (icon !== false) {
    resolvedIcon =
      icon ||
      (iconMapping && iconMapping[baseSeverity]) ||
      defaultIconMapping[baseSeverity];
  }

  const currentStyle = styles[variant][baseSeverity];
  const currentIconColor = styles[variant].iconColor[baseSeverity];

  const closeButtonClasses =
    variant === "filled"
      ? "text-white hover:bg-white/20"
      : "text-gray-500 hover:bg-black/5";

  return (
    <div
      role={role}
      className={`flex font-sans text-sm rounded-lg p-4 ${currentStyle} ${className}`}
      {...other}
    >
      {resolvedIcon && (
        <div className={`flex-shrink-0 mr-3 mt-0.5 ${currentIconColor}`}>
          {resolvedIcon}
        </div>
      )}

      <div className="flex-grow py-0.5">{children}</div>

      {(action || onClose) && (
        <div className="flex-shrink-0 ml-3 flex items-start">
          {action ? (
            action
          ) : (
            <IconButton
              type="button"
              className={`inline-flex rounded-lg p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${closeButtonClasses}`}
              aria-label={closeText}
              onClick={onClose}
            >
              <XMarkIcon className="w-5 h-5" />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
}